import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { X, Upload } from 'lucide-react';
import { Camp, CampImage } from '../../utils/types';
import { useGetCampImageUploadUrl, useSaveCampImage, useDeleteCampImage } from '../../api/organizerAdmin';
import './CampImageUploadModal.css';

interface CampImageUploadModalProps {
  camp: Camp;
  isOpen: boolean;
  onClose: () => void;
  organizerId: string;
}

const IMAGE_TYPES = ['default', 'image_2', 'image_3', 'image_4', 'image_5', 'image_6'];
const MAX_IMAGES = 6;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function getNextImageType(images: CampImage[]): string | null {
  const used = new Set(images.map((i) => i.type));
  return IMAGE_TYPES.find((t) => !used.has(t)) ?? null;
}

const CampImageUploadModal: React.FC<CampImageUploadModalProps> = ({
  camp,
  isOpen,
  onClose,
  organizerId,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'saving'>('idle');
  const [deletingTypes, setDeletingTypes] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const getUploadUrl = useGetCampImageUploadUrl(organizerId);
  const saveCampImage = useSaveCampImage(organizerId);
  const deleteCampImage = useDeleteCampImage(organizerId);

  const isUploading = uploadStatus !== 'idle';
  const isAtMax = camp.images.length >= MAX_IMAGES;
  const dropzoneDisabled = isUploading || isAtMax;

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        const code = rejectedFiles[0]?.errors[0]?.code;
        if (code === 'file-too-large') {
          setError('File is too large. Maximum size is 10MB.');
        } else if (code === 'file-invalid-type') {
          setError('Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.');
        } else {
          setError('Invalid file. Please try again.');
        }
        return;
      }

      const file = acceptedFiles[0];
      if (!file) return;

      const imageType = getNextImageType(camp.images);
      if (!imageType) return;

      const objectUrl = URL.createObjectURL(file);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(objectUrl);
      setUploadStatus('uploading');

      try {
        const { url, fields, key } = await getUploadUrl.mutateAsync({ campId: camp.id });

        const region = (fields['x-amz-credential'] ?? '').split('/')[2];
        const uploadUrl = region
          ? url.replace('.s3.amazonaws.com', `.s3.${region}.amazonaws.com`)
          : url;

        const formData = new FormData();
        Object.entries(fields).forEach(([fieldKey, value]) => {
          formData.append(fieldKey, value);
        });
        formData.append('Content-Type', file.type);
        formData.append('file', file);

        const uploadResponse = await fetch(uploadUrl, { method: 'POST', body: formData });
        if (!uploadResponse.ok) throw new Error('Failed to upload image to storage.');

        setUploadStatus('saving');
        await saveCampImage.mutateAsync({ campId: camp.id, key: fields.key ?? key, imageType });

        setPreviewUrl(null);
        setUploadStatus('idle');
      } catch (err) {
        setPreviewUrl(null);
        setUploadStatus('idle');
        setError(err instanceof Error ? err.message : 'Failed to upload image. Please try again.');
      }
    },
    [camp.id, camp.images, getUploadUrl, previewUrl, saveCampImage]
  );

  const handleDelete = async (img: CampImage) => {
    setError(null);
    setDeletingTypes((prev) => new Set(prev).add(img.type));
    try {
      await deleteCampImage.mutateAsync({ campId: camp.id, imageType: img.type });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete image. Please try again.');
    } finally {
      setDeletingTypes((prev) => {
        const next = new Set(prev);
        next.delete(img.type);
        return next;
      });
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif'] },
    multiple: false,
    maxSize: MAX_FILE_SIZE,
    disabled: dropzoneDisabled,
  });

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isUploading) onClose();
  };

  if (!isOpen) return null;

  const statusText = uploadStatus === 'uploading'
    ? 'Uploading...'
    : uploadStatus === 'saving'
    ? 'Saving...'
    : null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="camp-image-upload-modal">
        <button className="modal-close-btn" onClick={onClose} disabled={isUploading}>
          <X />
        </button>

        <h2 className="camp-image-upload-modal__title">Manage Camp Images</h2>
        <p className="camp-image-upload-modal__subtitle">{camp.name}</p>

        {error && <div className="message message--error">{error}</div>}

        {camp.images.length > 0 && (
          <div className="camp-image-upload-modal__images-grid">
            {camp.images.map((img) => (
              <div
                key={img.type}
                className={`camp-image-upload-modal__image-item${deletingTypes.has(img.type) ? ' camp-image-upload-modal__image-item--deleting' : ''}`}
              >
                <img src={img.url} alt={camp.name} className="camp-image-upload-modal__image-thumb" />
                <button
                  className="camp-image-upload-modal__image-delete"
                  onClick={() => handleDelete(img)}
                  disabled={deletingTypes.has(img.type) || isUploading}
                  aria-label="Remove image"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {previewUrl && (
              <div className="camp-image-upload-modal__image-item camp-image-upload-modal__image-item--pending">
                <img src={previewUrl} alt="Uploading..." className="camp-image-upload-modal__image-thumb" />
              </div>
            )}
          </div>
        )}

        <p className="camp-image-upload-modal__image-count">
          {camp.images.length + (previewUrl ? 1 : 0)} / {MAX_IMAGES} images
        </p>

        {statusText && (
          <p className="camp-image-upload-modal__status camp-image-upload-modal__status--pending">
            <span className="camp-image-upload-modal__spinner" />
            {statusText}
          </p>
        )}

        <div
          {...getRootProps()}
          className={`camp-image-upload-modal__dropzone${isDragActive ? ' camp-image-upload-modal__dropzone--active' : ''}${dropzoneDisabled ? ' camp-image-upload-modal__dropzone--disabled' : ''}`}
        >
          <input {...getInputProps()} />
          <Upload className="camp-image-upload-modal__dropzone-icon" />
          {isAtMax ? (
            <p>Maximum of {MAX_IMAGES} images reached.</p>
          ) : isDragActive ? (
            <p>Drop the image here...</p>
          ) : (
            <p>
              Drag &amp; drop an image here, or{' '}
              <span className="camp-image-upload-modal__dropzone-link">click to select</span>
            </p>
          )}
          <p className="camp-image-upload-modal__dropzone-hint">
            JPEG, PNG, WebP, or GIF &mdash; max 10MB
          </p>
        </div>

        <div className="camp-image-upload-modal__actions">
          <button className="btn btn--secondary btn--md" onClick={onClose} disabled={isUploading}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampImageUploadModal;
